import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, switchMap, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { OidcService } from '../services/oidc.service';
import { JwtDecoderService } from '../services/jwt-decoder.service';
import { Constants } from '../../app.constants';

/**
 * Determines whether an auth failure (401/403) was caused by an expired or
 * missing access token rather than a genuine authorization problem.
 *
 * When the browser is idle for a long time the token expires and silent renew
 * can no longer keep it alive. On the next API call the (stale) token is
 * rejected by the gateway. In that case we should re-authenticate the user
 * instead of logging them out or sending them to the unauthorized page.
 *
 * Related: STARREP-2897
 */
function isSessionExpired(injector: Injector): boolean {
  const token = injector.get(AuthService).accessToken;
  if (!token) {
    return true;
  }
  return injector.get(JwtDecoderService).isExpired(token);
}

/**
 * Re-authenticates the user, preserving the current route so they return to
 * where they were after login. Mirrors AutoLoginByConfigIdGuard.
 */
function reAuthenticate(injector: Injector): void {
  sessionStorage.setItem(Constants.WEB_STORAGE_KEY.REDIRECT, injector.get(Router).url);
  injector.get(OidcService).authorize();
}

// Track if we're currently refreshing to avoid multiple simultaneous refresh attempts
let isRefreshing = false;

export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        console.warn('[AuthErrorInterceptor] 401 Unauthorized detected');

        // Don't try to refresh if already refreshing or if this is an auth endpoint
        if (isRefreshing || req.url.includes('/connect/') || req.url.includes('/oauth/')) {
          const authService = injector.get(AuthService);
          authService.logout();
          return throwError(() => error);
        }

        // Try silent renew first — the IdP session/refresh cookie may still be
        // valid even when the local access token has expired (e.g. long idle).
        isRefreshing = true;
        const oidcService = injector.get(OidcService);
        const sessionExpired = isSessionExpired(injector);

        console.log('[AuthErrorInterceptor] Attempting silent renew (sessionExpired=%s)...', sessionExpired);

        return oidcService.checkAuth().pipe(
          take(1),
          switchMap((loginResponse) => {
            isRefreshing = false;

            if (loginResponse.isAuthenticated) {
              console.log('[AuthErrorInterceptor] Token refreshed successfully - retrying request');
              return next(req);
            }

            // Silent renew failed — if session was expired, do full re-auth
            // (preserves current route) instead of logging out
            if (sessionExpired) {
              console.warn('[AuthErrorInterceptor] Silent renew failed, session expired - re-authenticating');
              reAuthenticate(injector);
              return throwError(() => error);
            }

            console.warn('[AuthErrorInterceptor] Cannot refresh token - redirecting to login');
            oidcService.authorize();
            return throwError(() => error);
          }),
          catchError((refreshError) => {
            isRefreshing = false;
            console.error('[AuthErrorInterceptor] Error during token refresh:', refreshError);

            // On refresh failure with expired session, re-authenticate rather than logout
            if (sessionExpired) {
              reAuthenticate(injector);
              return throwError(() => error);
            }

            const authService = injector.get(AuthService);
            authService.logout();
            return throwError(() => error);
          })
        );
      }

      if (error.status === 403) {
        // Expired token on 403 — try silent renew before routing to unauthorized
        if (isSessionExpired(injector)) {
          const oidcService = injector.get(OidcService);
          return oidcService.checkAuth().pipe(
            take(1),
            switchMap((loginResponse) => {
              if (loginResponse.isAuthenticated) {
                return next(req);
              }
              reAuthenticate(injector);
              return throwError(() => error);
            }),
            catchError(() => {
              reAuthenticate(injector);
              return throwError(() => error);
            })
          );
        }

        const router = injector.get(Router);
        router.navigate(['/unauthorized']);
        return throwError(() => error);
      }

      return throwError(() => error);
    })
  );
};
